import { memoize } from "@omnia/shared/utils/memoize"
import { groupBy, isArrayEmpty} from "../../../omnia"
import { getTargetNutrientValueFromAnalysis, getTargetNutrientValueFromRequirement } from "../../../services/calcService"
import { ProductAppInput } from "../../../types"
import { ProductApplicationVm } from "./productApplicationVm"

/**
 * Returns a memoized function which indicates if the rates of an application (%, nutrient, product) can be
 * "synchronised", that is, calculated from the last input one
 *
 * @param Target app
 * @returns Function
 */
export const getCanSyncAllInputs = (app: ProductApplicationVm) => memoize(() => {

    const { ctxt, targetNutrient } = app

    const croppings = ctxt.getSelectedCropping()

    return {

        dependencies: [croppings, targetNutrient],

        /**
         * @returns True if rates can be synchronised, which is the case
         * if all crops in the plan have the same opening requirement
         * for the target nutrient
         */
        provider: () => {

            if (croppings.length === 1) return true

            const groups = groupBy(
                croppings.map(c => c.planRequirements.crop),
                c => getTargetNutrientValueFromRequirement(targetNutrient, c)
            )

            return (groups.length === 1)

        }

    }

})

/**
 * Synchronizes the various "rates" of an application, based on the last one used
 *
 * @param vm Application
 * @param reset If true and rates cannot be synchronised, then they will be reset
 */
export function syncRates(vm: ProductApplicationVm, reset: boolean): void {

    const { lastInputUsed, productApp: app, nutrientRate, productRate, targetNutrient, ctxt, analysis } = vm

    const crops = ctxt.getSelectedCropping()
    const canSync = vm.canSyncAllInputs()

    if ((!canSync) || isArrayEmpty(crops)) {

        if (reset) {
            // Reset inputs except the last one changed
            if (lastInputUsed !== ProductAppInput.NutrientPct) {
                app.NutrientPct = null;
            }
            if (lastInputUsed !== ProductAppInput.NutrientRate) {
                app.NutrientRate = null;
            }
            if (lastInputUsed !== ProductAppInput.ProductRate) {
                app.ProductRate = null;
            }
        }

        if (lastInputUsed === ProductAppInput.NutrientRate) {

            app.ProductRate = vm.calculateAverageProductRate()

        } else if (lastInputUsed === ProductAppInput.ProductRate) {

            app.NutrientRate = vm.calculateAverageNutrientRate()

        }

    }

    else {

        const cropping = crops[0]
        const productNutrientAnalysis = getTargetNutrientValueFromAnalysis(targetNutrient, analysis);

        const requirement = cropping.planRequirements.crop;
        const requirementForNutrient = getTargetNutrientValueFromRequirement(targetNutrient, requirement);

        if (lastInputUsed === ProductAppInput.NutrientPct) {

            app.NutrientRate = vm.calculateAverageNutrientRate()
            app.ProductRate = vm.calculateAverageProductRate()

        } else if (lastInputUsed === ProductAppInput.NutrientRate) {

            const productRate = vm.calculateAverageProductRate()

            app.NutrientPct = requirementForNutrient > 0 ? nutrientRate / requirementForNutrient : 0;
            app.ProductRate = productRate

        } else if (lastInputUsed === ProductAppInput.ProductRate) {

            app.NutrientPct = requirementForNutrient > 0 ? productRate * productNutrientAnalysis / requirementForNutrient : 0;
            app.NutrientRate = vm.calculateAverageNutrientRate()

        }

    }

}
